import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { QuickHelp, PAGE_TIPS } from '../components/HelpComponents';
import { CelebrationModal } from '../components/Celebration';
import { useBadgeChecker, BadgeNotification } from '../components/Badges';
import { UpgradeModal } from '../components/UpgradeModal';
import { GuapoVisionModal } from '../components/GuapoVisionModal';
import { apiGet, apiPost, apiPut, apiDelete } from '@/utils/api';
import { playCelebration } from '@/utils/audio';
import confetti from 'canvas-confetti';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

// ============== CELEBRATION UTILITIES ==============

// Task 4: Confetti burst for debt payoff celebrations
const triggerConfetti = () => {
  // Fire multiple confetti bursts for dramatic effect
  const count = 200;
  const defaults = {
    origin: { y: 0.7 },
    zIndex: 9999,
  };

  function fire(particleRatio, opts) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio),
    });
  }

  // Golden confetti burst
  fire(0.25, { spread: 26, startVelocity: 55, colors: ['#FFD700', '#FFA500'] });
  fire(0.2, { spread: 60, colors: ['#00FF00', '#32CD32'] });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8, colors: ['#FF69B4', '#FF1493', '#FFD700'] });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2, colors: ['#00CED1', '#20B2AA'] });
  fire(0.1, { spread: 120, startVelocity: 45, colors: ['#9370DB', '#8A2BE2'] });
};

// Task 4: Play success sound for debt payoff
const playSuccessSound = () => {
  try {
    // Use a reliable public domain sound URL (coin/cha-ching effect)
    const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {
      // Silently fail if autoplay is blocked
      console.log('Audio playback blocked by browser');
    });
  } catch (error) {
    console.log('Audio not supported');
  }
};

// Guapo Character Image URL
const GUAPO_IMAGE_URL = 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/qhory5fo_Guap.png';

// Debounce hook for delayed updates
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
}

// Helper to format currency with proper decimal places
const formatCurrency = (value) => {
  return `$${Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

/**
 * Task 1: Detect Negative Amortization
 * Checks if any debt's first-month interest >= minimum payment
 * Returns { hasDeficit, offendingDebtNames, totalMonthlyDeficit }
 */
const detectNegativeAmortization = (debts) => {
  if (!debts || debts.length === 0) {
    return { hasDeficit: false, offendingDebtNames: [], totalMonthlyDeficit: 0 };
  }
  
  const offendingDebts = [];
  let totalDeficit = 0;
  
  for (const debt of debts) {
    if (debt.balance > 0 && debt.apr > 0) {
      // Calculate first month interest: Balance * (APR / 100) / 12
      const monthlyInterest = (debt.balance * (debt.apr / 100)) / 12;
      const minPayment = debt.min_payment || 0;
      
      // Check if interest >= minimum payment (negative amortization)
      if (monthlyInterest >= minPayment) {
        offendingDebts.push({
          name: debt.name,
          balance: debt.balance,
          apr: debt.apr,
          monthlyInterest: monthlyInterest,
          minPayment: minPayment,
          deficit: monthlyInterest - minPayment
        });
        totalDeficit += (monthlyInterest - minPayment);
      }
    }
  }
  
  return {
    hasDeficit: offendingDebts.length > 0,
    offendingDebtNames: offendingDebts.map(d => d.name),
    offendingDebts: offendingDebts,
    totalMonthlyDeficit: Math.ceil(totalDeficit) // Round up to ensure coverage
  };
};

function DebtSnowball() {
  const [debts, setDebts] = useState([]);
  const [extraPayment, setExtraPayment] = useState(100);
  const { checkBadges, newBadge, dismissBadge } = useBadgeChecker();
  const [strategy, setStrategy] = useState('avalanche');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [oneTimePayments, setOneTimePayments] = useState([]); // [{month: 3, amount: 500}, ...]
  const [showOneTimeForm, setShowOneTimeForm] = useState(false);
  const [newOneTime, setNewOneTime] = useState({ month: 1, amount: 0 });
  
  // Edit mode state - track which debt is being edited (Task 1: Modal-based editing)
  const [editingDebt, setEditingDebt] = useState(null); // Now holds the FULL debt object for modal
  const [editFormData, setEditFormData] = useState({});
  const [pendingUpdates, setPendingUpdates] = useState({});
  const updateTimeoutRef = useRef({});
  
  // Task 3: Snowball Reallocation Dialog state
  const [showSnowballDialog, setShowSnowballDialog] = useState(false);
  const [debtToMarkPaid, setDebtToMarkPaid] = useState(null);
  
  // Task 2: Guapo Tip sheet for balance transfer education
  const [showGuapoTip, setShowGuapoTip] = useState(false);
  
  // Guapo Vision (Premium Feature) state
  const [isPremium, setIsPremium] = useState(false); // Mock premium state for testing
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showGuapoVisionModal, setShowGuapoVisionModal] = useState(false);
  
  const [newDebt, setNewDebt] = useState({
    name: '',
    balance: 0,
    apr: 0,
    min_payment: 0,
    debt_type: 'credit_card',
    due_day: 1,
    category: 'LOANS',
    show_on_dashboard: true,
    annual_fee: 0,
  });
  
  // Payment history from Dashboard's "Attack Debt" feature
  const [paymentHistory, setPaymentHistory] = useState({ payments: [], total_extra_paid: 0 });
  const [loadingHistory, setLoadingHistory] = useState(true);
  
  // Celebration state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);

  useEffect(() => {
    loadDebts();
    loadPaymentHistory();
  }, []);
  
  const loadPaymentHistory = async () => {
    try {
      const response = await apiGet('/debt/payment-history');
      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error('Error loading payment history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadDebts = async () => {
    try {
      const response = await apiGet('/debt');
      if (response.ok) {
        const data = await response.json();
        setDebts(data);
      }
    } catch (error) {
      console.error('Error loading debts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDebt = async () => {
    if (!newDebt.name.trim()) {
      alert('Please enter a debt name');
      return;
    }

    try {
      const response = await apiPost('/debt', newDebt);
      if (response.ok) {
        loadDebts();
        setShowAddForm(false);
        setNewDebt({
          name: '',
          balance: 0,
          apr: 0,
          min_payment: 0,
          debt_type: 'credit_card',
          due_day: 1,
          category: 'LOANS',
          show_on_dashboard: true,
        });
      }
    } catch (error) {
      console.error('Error adding debt:', error);
    }
  };

  // Start editing a debt (Task 1: Opens modal with full debt object)
  const startEditingDebt = (debt) => {
    setEditingDebt(debt); // Store full debt object for modal
    setEditFormData({
      name: debt.name,
      balance: debt.balance,
      apr: debt.apr,
      min_payment: debt.min_payment,
      debt_type: debt.debt_type,
      due_day: debt.due_day || 1,
    });
  };

  // Cancel editing (closes modal)
  const cancelEditing = () => {
    setEditingDebt(null);
    setEditFormData({});
  };

  // Save edited debt (Task 1: Save and recalculate)
  const saveEditedDebt = async () => {
    if (!editingDebt) return;
    
    const debtId = editingDebt.id || editingDebt;
    const currentDebt = debts.find(d => d.id === debtId);
    const isPayingOff = editFormData.balance !== undefined && editFormData.balance <= 0 && currentDebt?.balance > 0;
    
    try {
      const response = await apiPut(`/debt/${debtId}`, editFormData);
      if (response.ok) {
        // Trigger celebration if debt was paid off
        if (isPayingOff && currentDebt) {
          triggerConfetti();
          playSuccessSound();
          setCelebrationData({
            debtName: currentDebt.name,
            originalBalance: currentDebt.balance
          });
          setShowCelebration(true);
          playCelebration();
          checkBadges();
        }
        loadDebts();
        setEditingDebt(null);
        setEditFormData({});
      }
    } catch (error) {
      console.error('Error updating debt:', error);
    }
  };

  // Debounced inline update (for quick edits without edit mode)
  const handleInlineUpdate = (debtId, field, value) => {
    // Clear existing timeout for this debt
    if (updateTimeoutRef.current[debtId]) {
      clearTimeout(updateTimeoutRef.current[debtId]);
    }
    
    // Update local state immediately for responsive UI
    setDebts(prevDebts => prevDebts.map(d => 
      d.id === debtId ? { ...d, [field]: value } : d
    ));
    
    // Mark as pending update
    setPendingUpdates(prev => ({ ...prev, [debtId]: true }));
    
    // Debounce the API call
    updateTimeoutRef.current[debtId] = setTimeout(async () => {
      try {
        const currentDebt = debts.find(d => d.id === debtId);
        const isPayingOff = field === 'balance' && value <= 0 && currentDebt?.balance > 0;
        
        const response = await apiPut(`/debt/${debtId}`, { [field]: value });
        
        if (response.ok) {
          if (isPayingOff && currentDebt) {
            setCelebrationData({
              debtName: currentDebt.name,
              originalBalance: currentDebt.balance
            });
            setShowCelebration(true);
            playCelebration(); // Play user's preferred celebration sound
            checkBadges();
          }
        }
      } catch (error) {
        console.error('Error updating debt:', error);
      } finally {
        setPendingUpdates(prev => ({ ...prev, [debtId]: false }));
      }
    }, 800); // 800ms debounce
  };

  const handleUpdateDebt = async (debtId, updates) => {
    // Check if this update sets balance to 0 (debt paid off!)
    const currentDebt = debts.find(d => d.id === debtId);
    const isPayingOff = updates.balance !== undefined && updates.balance <= 0 && currentDebt?.balance > 0;
    
    try {
      const response = await apiPut(`/debt/${debtId}`, updates);
      if (response.ok) {
        // Trigger celebration if debt was paid off
        if (isPayingOff && currentDebt) {
          setCelebrationData({
            debtName: currentDebt.name,
            originalBalance: currentDebt.balance
          });
          setShowCelebration(true);
          playCelebration(); // Play user's preferred celebration sound
          // Check for new badges
          checkBadges();
        }
        loadDebts();
      }
    } catch (error) {
      console.error('Error updating debt:', error);
    }
  };

  const handleDeleteDebt = async (debtId) => {
    if (!window.confirm('Are you sure you want to delete this debt?')) return;
    
    try {
      const response = await apiDelete(`/debt/${debtId}`);
      if (response.ok) {
        loadDebts();
      }
    } catch (error) {
      console.error('Error deleting debt:', error);
    }
  };

  const handleTogglePaidThisMonth = async (debtId, isPaid) => {
    // Update local state immediately for responsive UI
    setDebts(prevDebts => prevDebts.map(d => 
      d.id === debtId ? { ...d, paid_this_month: isPaid } : d
    ));
    
    try {
      const response = await apiPut(`/debt/${debtId}`, { paid_this_month: isPaid });
      
      if (response.ok && isPaid) {
        // Show a quick toast or celebration
        const debt = debts.find(d => d.id === debtId);
        if (debt) {
          // Could add a mini celebration here
          console.log(`Marked ${debt.name} as paid! `);
        }
      }
    } catch (error) {
      console.error('Error updating debt payment status:', error);
      // Revert local state on error
      setDebts(prevDebts => prevDebts.map(d => 
        d.id === debtId ? { ...d, paid_this_month: !isPaid } : d
      ));
    }
  };

  // Task 2 & 3: Handler to initiate "Mark Paid" flow with snowball reallocation dialog
  const handleMarkDebtPaid = (debt) => {
    setDebtToMarkPaid(debt);
    setShowSnowballDialog(true);
  };

  // Task 3: Complete the debt payoff with optional snowball reallocation
  const completeDebtPayoff = async (rollIntoSnowball) => {
    if (!debtToMarkPaid) return;
    
    const minPaymentToRoll = debtToMarkPaid.min_payment || 0;
    const originalBalance = debtToMarkPaid.balance || 0;
    
    try {
      // Set balance to 0, mark as fully paid, and store original balance
      const response = await apiPut(`/debt/${debtToMarkPaid.id}`, { 
        balance: 0,
        isPaid: true,
        original_balance: originalBalance // Store for Victories display
      });
      
      if (response.ok) {
        // Task 4: Fire celebration effects
        triggerConfetti();
        playSuccessSound();
        
        // Show celebration modal
        setCelebrationData({
          debtName: debtToMarkPaid.name,
          originalBalance: originalBalance
        });
        setShowCelebration(true);
        playCelebration();
        checkBadges();
        
        // Task 3: If user chose to roll min payment into snowball
        if (rollIntoSnowball && minPaymentToRoll > 0) {
          setExtraPayment(prev => prev + minPaymentToRoll);
        }
        
        // Reload debts to get updated state
        loadDebts();
      }
    } catch (error) {
      console.error('Error completing debt payoff:', error);
    } finally {
      setShowSnowballDialog(false);
      setDebtToMarkPaid(null);
    }
  };

  // Guapo Vision: Handle Auto-Scan button click
  const handleAutoScanClick = () => {
    if (isPremium) {
      // Premium user: open the Guapo Vision scanner modal
      setShowGuapoVisionModal(true);
    } else {
      // Free user: show upgrade paywall
      setShowUpgradeModal(true);
    }
  };

  // Guapo Vision: Handle successful scan - auto-fill the Add Debt form
  const handleScanComplete = (result) => {
    console.log('Scan complete:', result);
    
    if (result.success && result.useData) {
      // Auto-populate the Add Debt form with extracted data
      setNewDebt(prev => ({
        ...prev,
        name: result.accountName || prev.name,
        balance: result.balance || prev.balance,
        apr: result.apr || prev.apr,
        min_payment: result.minPayment || prev.min_payment,
        debt_type: 'credit_card', // Assume credit card since we're scanning statements
      }));
      
      // Open the Add Debt form so user can review and submit
      setShowAddForm(true);
    }
    
    setShowGuapoVisionModal(false);
  };

  const calculatePayoff = useCallback(async () => {
    const validDebts = debts.filter((d) => d.balance > 0);
    if (validDebts.length === 0) {
      return;
    }

    setCalculating(true);
    try {
      const response = await apiPost('/debt/snowball', {
        debts: validDebts.map((d) => ({
          name: d.name,
          balance: d.balance,
          apr: d.apr,
          min_payment: d.min_payment,
        })),
        extra_payment: extraPayment,
        strategy: strategy,
        one_time_payments: oneTimePayments,
      });
      if (response.ok) {
        const data = await response.json();
        setResult(data);
      }
    } catch (error) {
      console.error('Error calculating payoff:', error);
    } finally {
      setCalculating(false);
    }
  }, [debts, extraPayment, strategy, oneTimePayments]);

  // Auto-recalculate when strategy, extra payment, or one-time payments change
  useEffect(() => {
    if (result && debts.filter(d => d.balance > 0).length > 0) {
      const timeoutId = setTimeout(() => {
        calculatePayoff();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [extraPayment, strategy, oneTimePayments]); // eslint-disable-line react-hooks/exhaustive-deps

  const addOneTimePayment = () => {
    if (newOneTime.amount > 0 && newOneTime.month > 0) {
      setOneTimePayments([...oneTimePayments, { ...newOneTime }]);
      setNewOneTime({ month: 1, amount: 0 });
      setShowOneTimeForm(false);
    }
  };

  const removeOneTimePayment = (index) => {
    setOneTimePayments(oneTimePayments.filter((_, i) => i !== index));
  };

  const downloadPayoffPlan = () => {
    if (!result || !result.schedule) return;
    
    let csv = 'Month,Remaining Debt,Payment Power,Interest Paid,% Complete\n';
    result.schedule.forEach((item) => {
      csv += `${item.month || 'N/A'},$${(item.remaining_debt || 0).toFixed(2)},$${(item.snowball_size || 0).toFixed(2)},$${(item.interest_paid || 0).toFixed(2)},${item.percent_complete || 0}%\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debt-payoff-plan-${strategy}.csv`;
    a.click();
  };

  const exportToExcel = async () => {
    const validDebts = debts.filter((d) => d.balance > 0);
    if (validDebts.length === 0) {
      alert('Please add some debts first');
      return;
    }

    try {
      const response = await apiPost('/export/debt-schedule', {
        debts: validDebts.map((d) => ({
          name: d.name,
          balance: d.balance,
          apr: d.apr,
          min_payment: d.min_payment,
          debt_type: d.debt_type || 'other',
        })),
        extra_payment: extraPayment,
        strategy: strategy,
        one_time_payments: oneTimePayments,
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Debt_Payoff_Plan_${strategy}_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to export. Please try again.');
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting. Please try again.');
    }
  };

  const getDebtTypeIcon = (type) => {
    const icons = {
      credit_card: '💳',
      personal_loan: '💰',
      student_loan: '🎓',
      medical_debt: '🏥',
      other: '📋'
    };
    return icons[type] || '📋';
  };

  const totalDebt = debts.reduce((sum, d) => sum + (d.balance || 0), 0);
  const totalMinPayment = debts.reduce((sum, d) => sum + (d.min_payment || 0), 0);
  const avgAPR = debts.length > 0 
    ? debts.reduce((sum, d) => sum + (d.apr || 0), 0) / debts.length 
    : 0;
  
  // Task 2: Split debts into Active (balance > 0 or not isPaid) and Victories (isPaid or balance = 0)
  const activeDebts = useMemo(() => {
    return debts.filter(d => d.balance > 0 && !d.isPaid);
  }, [debts]);
  
  const victoriesDebts = useMemo(() => {
    return debts.filter(d => d.balance <= 0 || d.isPaid);
  }, [debts]);
  
  // Task 1: Sort debts based on selected strategy (Avalanche = APR desc, Snowball = Balance asc)
  const sortedDebts = useMemo(() => {
    return [...activeDebts].sort((a, b) => {
      if (strategy === 'avalanche') {
        // Sort by APR descending (highest first), balance descending as tie-breaker
        if (b.apr !== a.apr) return b.apr - a.apr;
        return b.balance - a.balance;
      } else {
        // Snowball: Sort by balance ascending (lowest first)
        return a.balance - b.balance;
      }
    });
  }, [activeDebts, strategy]);
  
  // Guapo Tip: Find the debt with the highest APR for balance transfer education
  const highestAprDebt = useMemo(() => {
    if (activeDebts.length === 0) return null;
    return activeDebts.reduce((max, debt) => 
      (debt.apr > (max?.apr || 0)) ? debt : max
    , null);
  }, [activeDebts]);
  
  // Task 3: Calculate total monthly commitment (Min + Extra)
  const totalMonthlyCommitment = totalMinPayment + extraPayment;
  
  // Task 7: Check if schedule has any one-time payments
  const hasOneTimePayments = useMemo(() => {
    if (!result?.schedule) return false;
    return result.schedule.some(item => (item.one_time_payment || 0) > 0);
  }, [result]);
  
  // Task 1: Detect negative amortization (interest >= min payment)
  const negativeAmortization = useMemo(() => {
    return detectNegativeAmortization(debts);
  }, [debts]);
  
  // Calculate the fix amount (deficit + $10 buffer)
  const fixAmount = negativeAmortization.hasDeficit 
    ? negativeAmortization.totalMonthlyDeficit + 10 
    : 0;
  
  // Check if Guapo's intervention should show (deficit exists AND extra payment is insufficient)
  const showGuapoIntervention = negativeAmortization.hasDeficit && extraPayment < fixAmount;
  
  // Task 3: Handler to auto-fix negative amortization
  const handleGuapoFix = useCallback(() => {
    setExtraPayment(fixAmount);
    // Scroll to calculate button after fixing
    setTimeout(() => {
      const calculateBtn = document.querySelector('[data-testid="calculate-button"]');
      if (calculateBtn) {
        calculateBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  }, [fixAmount]);
  
  // Ref for sticky button observer (Task 4)
  const strategyRef = useRef(null);
  const [isStrategySectionVisible, setIsStrategySectionVisible] = useState(true);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStrategySectionVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );
    
    if (strategyRef.current) {
      observer.observe(strategyRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Badge Notification */}
      {newBadge && <BadgeNotification badge={newBadge} onClose={dismissBadge} />}
      
      {/* Celebration Modal for Paid Off Debts */}
      <CelebrationModal
        isOpen={showCelebration}
        onClose={() => setShowCelebration(false)}
        title="Debt Destroyed!"
        emoji="💥"
        message={celebrationData ? `You just paid off "${celebrationData.debtName}"! That's $${celebrationData.originalBalance?.toLocaleString()} of debt eliminated. You're one step closer to financial freedom!` : ''}
        ctaText="Onward to Victory! 🚀"
        showShareButton={true}
        shareText={celebrationData ? `I just paid off my "${celebrationData.debtName}" debt of $${celebrationData.originalBalance?.toLocaleString()}! One less debt standing in my way. #DebtFree #FinancialFreedom` : ''}
      />
      
      {/* Task 1: Edit Debt Modal */}
      <Dialog open={!!editingDebt} onOpenChange={(open) => !open && cancelEditing()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>{editingDebt ? getDebtTypeIcon(editingDebt.debt_type) : '📋'}</span>
              Edit Debt
            </DialogTitle>
            <DialogDescription>
              Update the details for this debt. Changes will trigger a recalculation of your payoff schedule.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Debt Name</label>
              <input
                type="text"
                value={editFormData.name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Chase Visa"
                data-testid="edit-debt-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Balance ($)</label>
                <input
                  type="number"
                  value={editFormData.balance || 0}
                  onChange={(e) => setEditFormData({ ...editFormData, balance: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="edit-debt-balance"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">APR (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={editFormData.apr || 0}
                  onChange={(e) => setEditFormData({ ...editFormData, apr: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="edit-debt-apr"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Payment ($)</label>
                <input
                  type="number"
                  value={editFormData.min_payment || 0}
                  onChange={(e) => setEditFormData({ ...editFormData, min_payment: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="edit-debt-min-payment"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Day (1-31)</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={editFormData.due_day || 1}
                  onChange={(e) => setEditFormData({ ...editFormData, due_day: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="edit-debt-due-day"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Debt Type</label>
              <select
                value={editFormData.debt_type || 'credit_card'}
                onChange={(e) => setEditFormData({ ...editFormData, debt_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="edit-debt-type"
              >
                <option value="credit_card">💳 Credit Card</option>
                <option value="personal_loan">💰 Personal Loan</option>
                <option value="student_loan">🎓 Student Loan</option>
                <option value="auto_loan">🚗 Auto Loan</option>
                <option value="medical_debt">🏥 Medical</option>
                <option value="other">📋 Other</option>
              </select>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <button
              type="button"
              onClick={cancelEditing}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              data-testid="edit-debt-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEditedDebt}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              data-testid="edit-debt-save"
            >
              Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Task 3: Snowball Reallocation Dialog */}
      <Dialog open={showSnowballDialog} onOpenChange={setShowSnowballDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <span className="text-2xl">🎉</span>
              Awesome Job!
            </DialogTitle>
            <DialogDescription asChild>
              <div className="pt-2">
                <p className="text-gray-700">
                  You're marking <span className="font-bold text-green-600">{debtToMarkPaid?.name}</span> as fully paid off!
                </p>
                {debtToMarkPaid?.min_payment > 0 && (
                  <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium mb-2">
                      Roll your freed-up payment into your snowball?
                    </p>
                    <p className="text-sm text-gray-600">
                      Add <span className="font-bold text-blue-600">${debtToMarkPaid?.min_payment?.toLocaleString()}</span> to your Extra Monthly Payment to crush your other debts even faster!
                    </p>
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4">
            <button
              type="button"
              onClick={() => completeDebtPayoff(false)}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              data-testid="snowball-no-btn"
            >
              No, just mark it paid
            </button>
            {debtToMarkPaid?.min_payment > 0 && (
              <button
                type="button"
                onClick={() => completeDebtPayoff(true)}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-emerald-700 transition-all"
                data-testid="snowball-yes-btn"
              >
                Yes, supercharge my snowball!
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Task 2: Guapo Tip Sheet for Balance Transfer Education */}
      <Sheet open={showGuapoTip} onOpenChange={setShowGuapoTip}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh]">
          <SheetHeader className="text-left">
            <div className="flex items-center gap-3 mb-2">
              <img 
                src={GUAPO_IMAGE_URL}
                alt="Guapo"
                className="w-12 h-12 rounded-full object-cover border-2 border-yellow-400 shadow-md"
              />
              <div>
                <SheetTitle className="text-lg">Guapo Tip</SheetTitle>
                <p className="text-xs text-gray-500">Your Finance Assistant</p>
              </div>
            </div>
          </SheetHeader>
          
          <div className="mt-4 space-y-4">
            <div className="p-4 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border border-yellow-200">
              <p className="text-gray-800 leading-relaxed">
                <span className="font-semibold text-amber-700">You're paying a lot of interest here!</span>
                {' '}If your credit score is in good shape, you might want to look into a <span className="font-semibold">0% APR Balance Transfer credit card</span> or a <span className="font-semibold">debt consolidation loan</span>.
              </p>
            </div>
            
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <p className="text-green-800 text-sm leading-relaxed">
                Moving this balance could save you <span className="font-bold">thousands in interest</span> and help you crush this debt months faster. Just watch out for balance transfer fees (usually 3-5%).
              </p>
            </div>
            
            {highestAprDebt && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p className="text-gray-600">
                  Your highest APR debt: <span className="font-bold text-red-600">{highestAprDebt.name}</span> at <span className="font-bold">{highestAprDebt.apr}% APR</span>
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  Monthly interest cost: ~${((highestAprDebt.balance * (highestAprDebt.apr / 100)) / 12).toFixed(2)}
                </p>
              </div>
            )}
          </div>
          
          <div className="mt-6">
            <button
              onClick={() => setShowGuapoTip(false)}
              className="w-full py-3 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 transition-colors"
            >
              Got it, thanks Guapo!
            </button>
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Guapo Vision: Upgrade Modal (Paywall) */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        feature="guapo_vision"
      />
      
      {/* Guapo Vision: Scanner Modal (Premium Users) */}
      <GuapoVisionModal
        isOpen={showGuapoVisionModal}
        onClose={() => setShowGuapoVisionModal(false)}
        onScanComplete={handleScanComplete}
      />
      
      {/* Header - More Compact */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-4 text-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold" data-testid="debt-calculator-title">
                ⚡ Debt Freedom Calculator
              </h1>
              <QuickHelp title="How to Use This Calculator" tips={PAGE_TIPS.debtSnowball} />
            </div>
            <p className="text-indigo-100 text-sm">
              Compare Snowball vs Avalanche methods
            </p>
          </div>
          {debts.length > 0 && (
            <button
              onClick={exportToExcel}
              data-testid="export-debt-header-btn"
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
          )}
        </div>
      </div>

      {/* Info Banner - Collapsible and Compact */}
      <details className="bg-blue-50 border border-blue-200 rounded-lg group">
        <summary className="flex items-center gap-2 p-3 cursor-pointer hover:bg-blue-100 rounded-lg transition-colors list-none">
          <span className="text-lg">🔄</span>
          <span className="font-medium text-blue-900 text-sm">Auto-Sync: Debts appear in Bills & Dashboard automatically</span>
          <svg className="w-4 h-4 ml-auto text-blue-600 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </summary>
        <div className="px-3 pb-3 pt-1 text-sm text-blue-700">
          Loans and credit cards you add here automatically appear in your Bills section and on your Dashboard. No need to enter them twice!
        </div>
      </details>

      {/* Quick Stats - Responsive Grid */}
      {debts.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 text-center">
            <p className="text-xs sm:text-sm text-gray-500">Total Debt</p>
            <p className="text-xl sm:text-2xl font-bold text-red-600">${totalDebt.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 text-center">
            <p className="text-xs sm:text-sm text-gray-500">Monthly Min</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">${totalMinPayment.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 text-center">
            <p className="text-xs sm:text-sm text-gray-500">Avg APR</p>
            <p className="text-xl sm:text-2xl font-bold text-orange-600">{avgAPR.toFixed(1)}%</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 text-center">
            <p className="text-xs sm:text-sm text-gray-500"># Debts</p>
            <p className="text-xl sm:text-2xl font-bold text-blue-600">{debts.length}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Debts Input */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Your Debts</h2>
              <div className="flex items-center gap-2">
                {/* Guapo Vision: Auto-Scan Statement Button (Premium Feature) */}
                <button
                  onClick={handleAutoScanClick}
                  data-testid="auto-scan-button"
                  className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-teal-700 transition-all text-sm flex items-center gap-1.5 relative group"
                >
                  <span>📸</span>
                  <span className="hidden sm:inline">Auto-Scan</span>
                  <span className="sm:hidden">Scan</span>
                  {/* Premium indicator */}
                  {!isPremium && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center text-xs shadow-sm">
                      👑
                    </span>
                  )}
                  {isPremium && (
                    <span className="text-yellow-300">✨</span>
                  )}
                </button>
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  data-testid="add-debt-button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                >
                  {showAddForm ? 'Cancel' : '+ Add Debt'}
                </button>
              </div>
            </div>

            {/* Add Form */}
            {showAddForm && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                    <select
                      value={newDebt.debt_type}
                      onChange={(e) => setNewDebt({ ...newDebt, debt_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="credit_card">💳 Credit Card</option>
                      <option value="personal_loan">💰 Personal Loan</option>
                      <option value="student_loan">🎓 Student Loan</option>
                      <option value="auto_loan">🚗 Auto Loan</option>
                      <option value="medical_debt">🏥 Medical</option>
                      <option value="other">📋 Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                    <input
                      type="text"
                      value={newDebt.name}
                      onChange={(e) => setNewDebt({ ...newDebt, name: e.target.value })}
                      placeholder="e.g., Chase Visa"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Balance ($)</label>
                    <input
                      type="number"
                      value={newDebt.balance || ''}
                      onChange={(e) => setNewDebt({ ...newDebt, balance: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">APR (%)</label>
                    <input
                      type="number"
                      value={newDebt.apr || ''}
                      onChange={(e) => setNewDebt({ ...newDebt, apr: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Min Payment ($)</label>
                    <input
                      type="number"
                      value={newDebt.min_payment || ''}
                      onChange={(e) => setNewDebt({ ...newDebt, min_payment: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Due Day (1-31)</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={newDebt.due_day || 1}
                      onChange={(e) => setNewDebt({ ...newDebt, due_day: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Annual Fee ($)</label>
                    <input
                      type="number"
                      value={newDebt.annual_fee || ''}
                      onChange={(e) => setNewDebt({ ...newDebt, annual_fee: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      data-testid="annual-fee-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                    <select
                      value={newDebt.category}
                      onChange={(e) => setNewDebt({ ...newDebt, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="LOANS">Loans</option>
                      <option value="UTILITIES">Utilities</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newDebt.show_on_dashboard}
                        onChange={(e) => setNewDebt({ ...newDebt, show_on_dashboard: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Show on Dashboard</span>
                    </label>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-4">
                  <button
                    onClick={handleAddDebt}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
                  >
                    Add Debt
                  </button>
                  <p className="text-xs text-gray-500">
                    💡 Debts with &quot;Show on Dashboard&quot; will appear as bills in your budget
                  </p>
                </div>
              </div>
            )}

            {/* Debts Table - Desktop / Card Layout - Mobile (Task 2) */}
            {activeDebts.length === 0 && victoriesDebts.length === 0 ? (
              <div className="text-center py-12 px-6 bg-gradient-to-br from-gray-50 to-indigo-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="text-5xl mb-4">💳</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No debts added yet</h3>
                <p className="text-gray-600 mb-4 max-w-md mx-auto">
                  Add your credit cards, loans, and other debts to create a personalized payoff plan. 
                  We&apos;ll show you exactly when you&apos;ll be debt-free!
                </p>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-all"
                >
                  + Add Your First Debt
                </button>
              </div>
            ) : (
              <>
                {/* Active Debts Section */}
                {activeDebts.length > 0 && (
                  <>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                      Active Debts ({activeDebts.length})
                    </h3>
                    
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full" data-testid="debts-table">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">This Month</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Debt</th>
                            <th className={`px-3 py-2 text-right text-xs font-semibold ${strategy === 'snowball' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'}`}>
                              Balance {strategy === 'snowball' && '↑'}
                            </th>
                            <th className={`px-3 py-2 text-right text-xs font-semibold ${strategy === 'avalanche' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'}`}>
                              APR % {strategy === 'avalanche' && '↓'}
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Min Pay</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Due Day</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {sortedDebts.map((debt) => (
                            <tr key={debt.id} className={`hover:bg-gray-50 ${debt.paid_this_month ? 'bg-green-50' : ''}`}>
                              <td className="px-3 py-2 text-center">
                                <button
                                  onClick={() => handleTogglePaidThisMonth(debt.id, !debt.paid_this_month)}
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                    debt.paid_this_month 
                                      ? 'bg-green-500 text-white shadow-sm' 
                                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                  }`}
                                  title={debt.paid_this_month ? 'Mark as unpaid' : 'Mark as paid this month'}
                                  data-testid={`debt-paid-checkbox-${debt.id}`}
                                >
                                  {debt.paid_this_month ? '✓' : '○'}
                                </button>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">{getDebtTypeIcon(debt.debt_type)}</span>
                                  <span className={`font-medium ${debt.paid_this_month ? 'text-green-700' : 'text-gray-900'}`}>{debt.name}</span>
                                  {debt.paid_this_month && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Paid ✓</span>
                                  )}
                                  {pendingUpdates[debt.id] && (
                                    <span className="text-xs text-blue-500">saving...</span>
                                  )}
                                </div>
                              </td>
                              <td className={`px-3 py-2 ${strategy === 'snowball' ? 'bg-blue-50/50' : ''}`}>
                                <input
                                  type="number"
                                  value={debt.balance}
                                  onChange={(e) => handleInlineUpdate(debt.id, 'balance', parseFloat(e.target.value) || 0)}
                                  className={`w-24 text-right font-medium text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none ${strategy === 'snowball' ? 'font-bold text-blue-700' : ''}`}
                                  title="Click to edit balance"
                                />
                              </td>
                              <td className={`px-3 py-2 ${strategy === 'avalanche' ? 'bg-blue-50/50' : ''}`}>
                                <div className="flex items-center justify-end gap-1">
                                  <input
                                    type="number"
                                    step="0.1"
                                    value={debt.apr}
                                    onChange={(e) => handleInlineUpdate(debt.id, 'apr', parseFloat(e.target.value) || 0)}
                                    className={`w-16 text-right text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none ${strategy === 'avalanche' ? 'font-bold text-blue-700' : ''}`}
                                    title="Click to edit APR"
                                  />
                                  {/* Guapo Tip: Show lightbulb icon for highest APR debt */}
                                  {highestAprDebt && highestAprDebt.id === debt.id && debt.apr >= 15 && (
                                    <button
                                      onClick={() => setShowGuapoTip(true)}
                                      className="w-6 h-6 flex items-center justify-center rounded-full bg-yellow-100 hover:bg-yellow-200 text-yellow-600 transition-all animate-pulse"
                                      title="Guapo has a tip!"
                                      data-testid="guapo-tip-btn"
                                    >
                                      💡
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={debt.min_payment}
                                  onChange={(e) => handleInlineUpdate(debt.id, 'min_payment', parseFloat(e.target.value) || 0)}
                                  className="w-20 text-right text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
                                  title="Click to edit minimum payment"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min="1"
                                  max="31"
                                  value={debt.due_day || 1}
                                  onChange={(e) => handleInlineUpdate(debt.id, 'due_day', parseInt(e.target.value) || 1)}
                                  className="w-14 text-center text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
                                  title="Click to edit due day"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleMarkDebtPaid(debt)}
                                    className="text-green-600 hover:text-green-800 hover:bg-green-50 px-2 py-1 rounded text-xs font-medium"
                                    title="Mark as fully paid off"
                                    data-testid={`mark-paid-btn-${debt.id}`}
                                  >
                                    Mark Paid
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => startEditingDebt(debt)}
                                    className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded text-sm"
                                    title="Edit all fields"
                                    data-testid={`edit-debt-btn-${debt.id}`}
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteDebt(debt.id)}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded text-sm"
                                    title="Delete debt"
                                    data-testid={`delete-debt-btn-${debt.id}`}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                
                    {/* Mobile Card Layout (Task 2) */}
                    <div className="md:hidden space-y-3" data-testid="debts-mobile-cards">
                      {sortedDebts.map((debt) => (
                        <div 
                          key={debt.id} 
                          className={`rounded-lg border p-4 ${
                            debt.paid_this_month ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                          }`}
                          data-testid={`debt-card-${debt.id}`}
                        >
                          {/* Header: Name & Balance */}
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{getDebtTypeIcon(debt.debt_type)}</span>
                              <div>
                                <h4 className={`font-semibold ${debt.paid_this_month ? 'text-green-800' : 'text-gray-900'}`}>
                                  {debt.name}
                                </h4>
                                {debt.paid_this_month && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Paid ✓</span>
                                )}
                              </div>
                            </div>
                            <div className={`text-right ${strategy === 'snowball' ? 'bg-blue-100 px-2 py-1 rounded-lg' : ''}`}>
                              <p className={`text-lg font-bold ${strategy === 'snowball' ? 'text-blue-700' : 'text-gray-900'}`}>
                                ${debt.balance.toLocaleString()}
                              </p>
                              <p className="text-xs text-gray-500">Balance</p>
                            </div>
                          </div>
                          
                          {/* Details Grid */}
                          <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                            <div className={`${strategy === 'avalanche' ? 'bg-blue-100 px-2 py-1 rounded-lg' : ''}`}>
                              <div className="flex items-center gap-1">
                                <p className="text-gray-500 text-xs">APR</p>
                                {/* Guapo Tip for mobile - highest APR */}
                                {highestAprDebt && highestAprDebt.id === debt.id && debt.apr >= 15 && (
                                  <button
                                    onClick={() => setShowGuapoTip(true)}
                                    className="w-5 h-5 flex items-center justify-center rounded-full bg-yellow-100 text-yellow-600 animate-pulse text-xs"
                                    data-testid="guapo-tip-btn-mobile"
                                  >
                                    💡
                                  </button>
                                )}
                              </div>
                              <p className={`font-medium ${strategy === 'avalanche' ? 'text-blue-700 font-bold' : 'text-gray-900'}`}>
                                {debt.apr}%
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs">Min Payment</p>
                              <p className="font-medium text-gray-900">${debt.min_payment}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs">Due Day</p>
                              <p className="font-medium text-gray-900">{debt.due_day || 1}</p>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleTogglePaidThisMonth(debt.id, !debt.paid_this_month)}
                                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                  debt.paid_this_month 
                                    ? 'bg-green-500 text-white' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {debt.paid_this_month ? '✓ This Month' : '○ This Month'}
                              </button>
                              <button
                                onClick={() => handleMarkDebtPaid(debt)}
                                className="px-2 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-xs font-medium"
                                data-testid={`mark-paid-mobile-${debt.id}`}
                              >
                                Mark Paid
                              </button>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => startEditingDebt(debt)}
                                className="px-2 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg text-sm"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDeleteDebt(debt.id)}
                                className="px-2 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                
                {/* Task 2: Victories / Paid Off Accordion */}
                {victoriesDebts.length > 0 && (
                  <div className="mt-6">
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="victories" className="border border-green-200 rounded-xl overflow-hidden bg-gradient-to-r from-green-50 to-emerald-50">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-green-100/50" data-testid="victories-accordion">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">🏆</span>
                            <div className="text-left">
                              <h3 className="font-semibold text-green-800">Victories / Paid Off</h3>
                              <p className="text-xs text-green-600">{victoriesDebts.length} debt{victoriesDebts.length !== 1 ? 's' : ''} conquered!</p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-3 pt-2">
                            {victoriesDebts.map((debt) => (
                              <div 
                                key={debt.id}
                                className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200"
                                data-testid={`victory-card-${debt.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                    <span className="text-lg">✓</span>
                                  </div>
                                  <div>
                                    <p className="font-medium text-green-800">{debt.name}</p>
                                    <p className="text-xs text-green-600">
                                      Original: ${(debt.original_balance || debt.balance || 0).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-green-600 font-bold">PAID</span>
                                  <button
                                    onClick={() => handleDeleteDebt(debt.id)}
                                    className="text-gray-400 hover:text-red-500 p-1"
                                    title="Remove from history"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Column - Strategy & Calculate */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" ref={strategyRef}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">⚙️ Your Strategy</h2>
            
            {/* Strategy Toggle */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Method</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setStrategy('avalanche')}
                  data-testid="strategy-avalanche-btn"
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    strategy === 'avalanche'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg mb-1">🏔️ Avalanche</div>
                  <div className="text-xs text-gray-600">Highest APR first</div>
                  <div className="text-xs text-blue-600 font-medium">Saves most $</div>
                </button>
                <button
                  onClick={() => setStrategy('snowball')}
                  data-testid="strategy-snowball-btn"
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    strategy === 'snowball'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg mb-1">⛄ Snowball</div>
                  <div className="text-xs text-gray-600">Smallest debt first</div>
                  <div className="text-xs text-green-600 font-medium">Quick wins</div>
                </button>
              </div>
            </div>

            {/* Extra Payment - Slider + Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Extra Monthly Payment
              </label>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  max="10000"
                  value={extraPayment}
                  onChange={(e) => setExtraPayment(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-right font-bold text-blue-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-500">/month</span>
              </div>
              
              {/* Quick presets */}
              <div className="flex gap-1 mb-2">
                {[0, 50, 100, 200, 300, 500].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setExtraPayment(amount)}
                    className={`flex-1 py-1 text-xs rounded transition-colors ${
                      extraPayment === amount
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
              
              <input
                type="range"
                min="0"
                max="1000"
                step="25"
                value={Math.min(extraPayment, 1000)}
                onChange={(e) => setExtraPayment(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>$0</span>
                <span>$500</span>
                <span>$1,000+</span>
              </div>
              {result && (
                <p className="text-xs text-green-600 mt-2 font-medium">
                  ✓ Schedule updates automatically
                </p>
              )}
            </div>

            {/* One-Time Payments Section */}
            <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  💰 One-Time Payments
                </label>
                <button
                  onClick={() => setShowOneTimeForm(!showOneTimeForm)}
                  className="text-xs text-yellow-700 hover:text-yellow-900 font-medium"
                >
                  {showOneTimeForm ? 'Cancel' : '+ Add'}
                </button>
              </div>
              
              <p className="text-xs text-gray-600 mb-2">
                Add bonuses, tax refunds, or extra payments for specific months
              </p>

              {showOneTimeForm && (
                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Month #</label>
                    <input
                      type="number"
                      min="1"
                      max="360"
                      value={newOneTime.month}
                      onChange={(e) => setNewOneTime({ ...newOneTime, month: parseInt(e.target.value) || 1 })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Amount ($)</label>
                    <input
                      type="number"
                      min="0"
                      value={newOneTime.amount || ''}
                      onChange={(e) => setNewOneTime({ ...newOneTime, amount: parseFloat(e.target.value) || 0 })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={addOneTimePayment}
                      className="px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              {oneTimePayments.length > 0 ? (
                <div className="space-y-1">
                  {oneTimePayments.map((payment, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm bg-white rounded px-2 py-1">
                      <span>Month {payment.month}: <span className="font-semibold text-yellow-700">${payment.amount}</span></span>
                      <button
                        onClick={() => removeOneTimePayment(idx)}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="text-xs text-yellow-700 font-medium mt-1">
                    Total: ${oneTimePayments.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic">No one-time payments added</p>
              )}
            </div>

            {/* Payment History from Dashboard */}
            {paymentHistory.payments.length > 0 && (
              <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-green-800">
                    ✅ Payments from Dashboard
                  </label>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                    Already Applied
                  </span>
                </div>
                
                <p className="text-xs text-gray-600 mb-2">
                  These payments were made via the Dashboard&apos;s &quot;Attack Debt&quot; feature and have already reduced your balances.
                </p>

                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {paymentHistory.payments.slice(0, 5).map((payment) => (
                    <div key={payment.id} className="flex justify-between items-center text-sm bg-white rounded px-2 py-1">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">🎯</span>
                        <span className="text-gray-700 truncate max-w-[120px]">{payment.debt_name}</span>
                      </div>
                      <span className="font-semibold text-green-700">${payment.amount.toLocaleString()}</span>
                    </div>
                  ))}
                  {paymentHistory.payments.length > 5 && (
                    <p className="text-xs text-gray-500 text-center pt-1">
                      +{paymentHistory.payments.length - 5} more payments
                    </p>
                  )}
                </div>
                
                <div className="mt-2 pt-2 border-t border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-green-800 font-medium">Total Extra Paid:</span>
                    <span className="text-sm font-bold text-green-700">${paymentHistory.total_extra_paid.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    💡 Your debt balances are already updated! No need to enter again.
                  </p>
                </div>
              </div>
            )}

            {/* Task 3: Total Monthly Commitment Tally */}
            <div className="mb-4 p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200" data-testid="total-commitment-tally">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-indigo-800">Total New Monthly Payment:</span>
                <span className="text-xl font-bold text-indigo-600" data-testid="total-commitment-value">
                  {formatCurrency(totalMonthlyCommitment)}
                </span>
              </div>
              <p className="text-xs text-indigo-600 mt-1">
                (${totalMinPayment.toLocaleString()} min + ${extraPayment.toLocaleString()} extra)
              </p>
            </div>

            {/* Calculate Button - Desktop */}
            <button
              onClick={calculatePayoff}
              disabled={calculating || debts.length === 0}
              data-testid="calculate-button"
              className="hidden md:block w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {calculating ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span> Calculating...
                </span>
              ) : (
                '🚀 Calculate Freedom Date'
              )}
            </button>
            
            {/* Calculate Button - Mobile (in-place) */}
            <button
              onClick={calculatePayoff}
              disabled={calculating || debts.length === 0}
              data-testid="calculate-button-mobile"
              className="md:hidden w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {calculating ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span> Calculating...
                </span>
              ) : (
                '🚀 Calculate Freedom Date'
              )}
            </button>
          </div>

          {/* Task 2: Guapo's Negative Amortization Intervention */}
          {showGuapoIntervention && debts.length > 0 && (
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl shadow-lg border-2 border-red-200 p-6" data-testid="guapo-intervention">
              {/* Chat bubble layout */}
              <div className="flex items-start gap-4">
                {/* Guapo Avatar */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img 
                      src={GUAPO_IMAGE_URL}
                      alt="Guapo"
                      className="w-14 h-14 rounded-full object-cover border-2 border-orange-300 shadow-md"
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Chat Bubble Content */}
                <div className="flex-1">
                  <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-sm border border-red-100">
                    <p className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                      <span>Wait a minute!</span>
                      <span className="text-lg">🚨</span>
                    </p>
                    <p className="text-gray-700 text-sm leading-relaxed mb-3">
                      I ran the math, and your current payments on{' '}
                      <span className="font-bold text-red-600">
                        {negativeAmortization.offendingDebtNames.join(', ')}
                      </span>{' '}
                      don't cover the monthly interest. <span className="font-semibold">You're going backward!</span>
                    </p>
                    <p className="text-gray-700 text-sm leading-relaxed mb-4">
                      I calculate you need at least{' '}
                      <span className="font-bold text-green-600 text-base">{formatCurrency(fixAmount)}</span>{' '}
                      more per month just to start shrinking this debt.
                    </p>
                    
                    {/* Breakdown */}
                    <div className="bg-red-50 rounded-lg p-3 mb-4 text-xs">
                      <p className="font-semibold text-red-700 mb-2">📊 The Math:</p>
                      {negativeAmortization.offendingDebts?.map((debt, idx) => (
                        <div key={idx} className="flex justify-between text-gray-600 mb-1">
                          <span>{debt.name}:</span>
                          <span>
                            <span className="text-red-600">${debt.monthlyInterest.toFixed(2)}/mo interest</span>
                            {' > '}
                            <span className="text-gray-500">${debt.minPayment.toFixed(2)} min payment</span>
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Task 3: Fix It Button */}
                    <button
                      onClick={handleGuapoFix}
                      data-testid="guapo-fix-button"
                      className="w-full py-3 px-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-bold text-sm shadow-md transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Fix it, Guapo (Add {formatCurrency(fixAmount)}/mo)
                    </button>
                  </div>
                  
                  {/* Guapo's name tag */}
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <span className="font-medium">Guapo</span>
                    <span>•</span>
                    <span>Your Finance Assistant</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Results Summary - Hidden when Guapo intervention showing */}
          {result && !showGuapoIntervention && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-6" data-testid="results-summary">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🎉 Your Results</h3>
              
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Debt Free Date</p>
                  <p className="text-2xl font-bold text-green-600">{result.debt_free_date}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-600">Months</p>
                    <p className="text-xl font-bold text-blue-600">{result.months_to_freedom}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-600">Interest</p>
                    <p className="text-xl font-bold text-orange-600">${result.total_interest_paid?.toLocaleString()}</p>
                  </div>
                </div>

                {/* Comparison */}
                {result.comparison && (
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-2">vs {result.comparison.alternate_strategy === 'avalanche' ? '🏔️ Avalanche' : '⛄ Snowball'}</p>
                    <div className="flex justify-between text-sm">
                      <span className={result.comparison.interest_difference > 0 ? 'text-green-600' : 'text-red-600'}>
                        {result.comparison.interest_difference > 0 ? '✓ Saves' : '✗ Costs'} ${Math.abs(result.comparison.interest_difference).toLocaleString()}
                      </span>
                      <span className={result.comparison.time_difference > 0 ? 'text-green-600' : 'text-red-600'}>
                        {result.comparison.time_difference > 0 ? '+' : ''}{result.comparison.time_difference} mo
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Section - Hidden when Guapo intervention showing */}
      {result && !showGuapoIntervention && (
        <div className="space-y-6">
          {/* Motivational Messages */}
          {result.messages && result.messages.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {result.messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`rounded-xl p-4 ${
                    msg.type === 'savings' ? 'bg-green-50 border border-green-200' :
                    msg.type === 'momentum' ? 'bg-blue-50 border border-blue-200' :
                    msg.type === 'strategy' ? 'bg-purple-50 border border-purple-200' :
                    'bg-yellow-50 border border-yellow-200'
                  }`}
                >
                  <p className={`text-sm font-medium ${
                    msg.type === 'savings' ? 'text-green-800' :
                    msg.type === 'momentum' ? 'text-blue-800' :
                    msg.type === 'strategy' ? 'text-purple-800' :
                    'text-yellow-800'
                  }`}>
                    {msg.type === 'savings' && '💰 '}
                    {msg.type === 'momentum' && '🎯 '}
                    {msg.type === 'strategy' && '📊 '}
                    {msg.type === 'acceleration' && '🚀 '}
                    {msg.text}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex border-b border-gray-200">
              {['overview', 'timeline', 'schedule'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {tab === 'overview' && '📊 Overview'}
                  {tab === 'timeline' && '📅 Timeline'}
                  {tab === 'schedule' && '📋 Full Schedule'}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Task 6: Debt Reduction Chart with dynamic y-axis and gradient fill */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Debt Reduction Over Time</h3>
                    <div className="relative h-48 bg-gray-50 rounded-lg p-4 overflow-hidden" data-testid="debt-reduction-chart">
                      {/* Gradient background fill */}
                      <div className="absolute inset-0 bg-gradient-to-t from-red-100/50 via-orange-50/30 to-transparent pointer-events-none" />
                      
                      <div className="relative h-full flex items-end gap-px">
                        {result.schedule.filter((_, i) => i % Math.ceil(result.schedule.length / 30) === 0).map((item, index, arr) => {
                          // Task 6: Dynamic y-axis scaling - use actual max from filtered data
                          const filteredData = result.schedule.filter((_, i) => i % Math.ceil(result.schedule.length / 30) === 0);
                          const maxDebt = Math.max(...filteredData.map(d => d.remaining_debt || 0));
                          const minDebt = Math.min(...filteredData.map(d => d.remaining_debt || 0));
                          // Scale to use full height (5% min height for visibility)
                          const range = maxDebt - minDebt || 1;
                          const normalizedHeight = ((item.remaining_debt || 0) - minDebt) / range;
                          const height = Math.max(5, normalizedHeight * 95 + 5);
                          
                          return (
                            <div key={index} className="flex-1 flex flex-col items-center group relative">
                              <div
                                className="w-full bg-gradient-to-t from-red-500 via-red-400 to-orange-400 rounded-t transition-all group-hover:opacity-80 shadow-sm"
                                style={{ height: `${height}%`, minHeight: '4px' }}
                              />
                              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                {item.month}: {formatCurrency(item.remaining_debt || 0)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>Today</span>
                      <span>{result.debt_free_date}</span>
                    </div>
                  </div>

                  {/* Task 5 & 6: Payment Power Growth with .toFixed(2) formatting and gradient fill */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Payment Power Growth (Snowball Effect)</h3>
                    <div className="relative h-48 bg-gray-50 rounded-lg p-4 overflow-hidden" data-testid="payment-power-chart">
                      {/* Gradient background fill */}
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-100/50 via-cyan-50/30 to-transparent pointer-events-none" />
                      
                      <div className="relative h-full flex items-end gap-px">
                        {result.schedule.filter((_, i) => i % Math.ceil(result.schedule.length / 30) === 0).map((item, index) => {
                          const maxSnowball = result.final_snowball || result.schedule[result.schedule.length - 1]?.snowball_size || 1;
                          const height = ((item.snowball_size || 0) / maxSnowball) * 100;
                          return (
                            <div key={index} className="flex-1 flex flex-col items-center group relative">
                              <div
                                className="w-full bg-gradient-to-t from-blue-500 via-blue-400 to-cyan-400 rounded-t transition-all group-hover:opacity-80 shadow-sm"
                                style={{ height: `${height}%`, minHeight: '4px' }}
                              />
                              {/* Task 5: Fix floating-point error with .toFixed(2) */}
                              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                {item.month}: {formatCurrency(item.snowball_size || 0)}/mo
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* Task 5: Fix floating-point formatting in axis labels */}
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>{formatCurrency(totalMinPayment + extraPayment)}/mo</span>
                      <span>{formatCurrency(result.final_snowball || 0)}/mo</span>
                    </div>
                  </div>

                  {/* Scenarios Comparison */}
                  {result.scenarios && result.scenarios.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">What If? Scenarios</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {result.scenarios.map((scenario, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-600">+${scenario.extra_payment}/mo</p>
                            <p className="text-lg font-bold text-gray-900">{scenario.months} mo</p>
                            <p className="text-xs text-gray-500">{formatCurrency(scenario.total_interest)} interest</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Timeline Tab - Gamified Visual Journey Map */}
              {activeTab === 'timeline' && (
                <div className="pb-4" data-testid="timeline-journey-map">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Your Debt Freedom Journey</h3>
                  <p className="text-xs text-gray-500 mb-6">Watch your progress as you crush each debt!</p>
                  
                  {/* Visual Journey Map */}
                  <div className="relative">
                    {/* Central winding path line */}
                    <div className="absolute left-1/2 top-0 bottom-0 w-1 transform -translate-x-1/2">
                      {result.milestones?.map((milestone, idx) => {
                        const isCompleted = (() => {
                          const now = new Date();
                          const currentMonth = now.getMonth();
                          const currentYear = now.getFullYear();
                          const milestoneDate = new Date(milestone.date + ' 1');
                          return milestoneDate <= now;
                        })();
                        const isNext = (() => {
                          if (isCompleted) return false;
                          const prevMilestones = result.milestones.slice(0, idx);
                          return prevMilestones.every(m => {
                            const mDate = new Date(m.date + ' 1');
                            return mDate <= new Date();
                          });
                        })();
                        
                        return (
                          <div
                            key={`line-${idx}`}
                            className={`absolute w-full ${isCompleted ? 'bg-green-400' : isNext ? 'bg-blue-400' : 'bg-gray-300 border-l-2 border-dashed border-gray-400'}`}
                            style={{
                              top: `${(idx / Math.max(result.milestones.length - 1, 1)) * 100}%`,
                              height: idx < result.milestones.length - 1 
                                ? `${(1 / Math.max(result.milestones.length - 1, 1)) * 100}%` 
                                : '0'
                            }}
                          />
                        );
                      })}
                    </div>
                    
                    {/* Journey Nodes */}
                    <div className="space-y-8">
                      {result.milestones?.map((milestone, idx) => {
                        // Determine status based on current date
                        const isCompleted = (() => {
                          const now = new Date();
                          const milestoneDate = new Date(milestone.date + ' 1');
                          return milestoneDate <= now;
                        })();
                        const isNext = (() => {
                          if (isCompleted) return false;
                          const prevMilestones = result.milestones.slice(0, idx);
                          return prevMilestones.every(m => {
                            const mDate = new Date(m.date + ' 1');
                            return mDate <= new Date();
                          });
                        })();
                        const isFuture = !isCompleted && !isNext;
                        
                        // Alternate left/right for winding path effect
                        const isLeft = idx % 2 === 0;
                        
                        // Node styling based on status
                        const nodeClasses = isCompleted 
                          ? 'bg-green-500 text-white shadow-lg ring-2 ring-green-300' 
                          : isNext 
                            ? 'bg-blue-500 text-white shadow-lg ring-4 ring-blue-300 animate-pulse' 
                            : 'bg-gray-200 text-gray-500';
                        
                        const cardClasses = isCompleted
                          ? 'bg-green-50 border-green-200'
                          : isNext
                            ? 'bg-blue-50 border-blue-300 shadow-md'
                            : 'bg-gray-50 border-gray-200 opacity-60';
                        
                        // Icon and label based on milestone type
                        let icon, label, sublabel;
                        if (milestone.type === 'debt_paid') {
                          icon = (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          );
                          label = `${milestone.name} DEFEATED!`;
                          sublabel = `$${(milestone.original_balance || 0).toLocaleString()} eliminated`;
                        } else if (milestone.type === 'snowball_powerup') {
                          icon = (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                            </svg>
                          );
                          label = `+$${(milestone.increase || 0).toLocaleString()}/mo unlocked!`;
                          sublabel = `Snowball grows to $${(milestone.new_snowball || 0).toLocaleString()}`;
                        } else if (milestone.type === 'round_milestone') {
                          icon = (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                            </svg>
                          );
                          label = `Under $${(milestone.threshold / 1000).toFixed(0)}K!`;
                          sublabel = `Only $${(milestone.remaining || 0).toLocaleString()} left`;
                        } else if (milestone.type === 'halfway') {
                          icon = (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                            </svg>
                          );
                          label = 'HALFWAY THERE!';
                          sublabel = '50% of debt destroyed';
                        } else if (milestone.type === 'debt_free') {
                          icon = (
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
                            </svg>
                          );
                          label = 'DEBT FREE!';
                          sublabel = 'Financial freedom achieved!';
                        }
                        
                        return (
                          <div 
                            key={idx} 
                            className={`relative flex items-center ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}
                            data-testid={`milestone-node-${idx}`}
                          >
                            {/* Card Content */}
                            <div className={`w-[42%] ${isLeft ? 'pr-4 text-right' : 'pl-4 text-left'}`}>
                              <div className={`rounded-lg border p-3 ${cardClasses} transition-all`}>
                                <p className={`text-xs font-bold ${isCompleted ? 'text-green-700' : isNext ? 'text-blue-700' : 'text-gray-500'}`}>
                                  {label}
                                </p>
                                <p className={`text-xs ${isCompleted ? 'text-green-600' : isNext ? 'text-blue-600' : 'text-gray-400'}`}>
                                  {sublabel}
                                </p>
                                <p className={`text-xs mt-1 ${isCompleted ? 'text-green-500' : isNext ? 'text-blue-500' : 'text-gray-400'}`}>
                                  {milestone.date}
                                </p>
                              </div>
                            </div>
                            
                            {/* Center Node */}
                            <div className="w-[16%] flex justify-center relative z-10">
                              <div 
                                className={`w-10 h-10 rounded-full flex items-center justify-center ${nodeClasses} transition-all`}
                                data-testid={`milestone-icon-${milestone.type}`}
                              >
                                {isCompleted && (
                                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                                {icon}
                              </div>
                            </div>
                            
                            {/* Empty space for opposite side */}
                            <div className="w-[42%]" />
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Empty state if no milestones */}
                    {(!result.milestones || result.milestones.length === 0) && (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm">Your journey awaits!</p>
                        <p className="text-xs">Add debts and calculate to see your path to freedom</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Payoff Order Summary */}
                  {result.debts_paid_off_order && result.debts_paid_off_order.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Attack Order</h4>
                      <div className="flex flex-wrap gap-2">
                        {result.debts_paid_off_order.map((debt, idx) => (
                          <div key={idx} className="bg-gradient-to-r from-gray-100 to-gray-50 rounded-full px-3 py-1.5 text-sm border border-gray-200 flex items-center gap-2">
                            <span className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </span>
                            <span className="font-medium text-gray-800">{debt.name}</span>
                            <span className="text-gray-400 text-xs">({debt.date})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Task 7: Schedule Tab with sticky Month column and conditional One-Time column */}
              {activeTab === 'schedule' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-semibold text-gray-700">Month-by-Month Schedule</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={downloadPayoffPlan}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                      >
                        📥 CSV
                      </button>
                      <button
                        onClick={exportToExcel}
                        data-testid="export-debt-excel-btn"
                        className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                      >
                        📊 Excel
                      </button>
                    </div>
                  </div>
                  <div className="max-h-96 overflow-auto" data-testid="schedule-table-container">
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          {/* Task 7: Sticky Month column header */}
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 bg-gray-50 sticky left-0 z-20 border-r border-gray-200">Month</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Remaining</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Payment Power</th>
                          {/* Task 7: Conditionally show One-Time column */}
                          {hasOneTimePayments && (
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">One-Time</th>
                          )}
                          <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Progress</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {result.schedule.map((item, idx) => (
                          <tr key={idx} className={`hover:bg-gray-50 ${item.one_time_payment > 0 ? 'bg-yellow-50' : ''}`}>
                            {/* Task 7: Sticky Month column */}
                            <td className="px-3 py-2 font-medium bg-white sticky left-0 border-r border-gray-100">{item.month}</td>
                            <td className="px-3 py-2 text-right text-red-600">{formatCurrency(item.remaining_debt || 0)}</td>
                            <td className="px-3 py-2 text-right text-blue-600">{formatCurrency(item.snowball_size || 0)}</td>
                            {/* Task 7: Conditionally show One-Time column */}
                            {hasOneTimePayments && (
                              <td className="px-3 py-2 text-right">
                                {(item.one_time_payment || 0) > 0 ? (
                                  <span className="text-yellow-600 font-semibold">+{formatCurrency(item.one_time_payment || 0)}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            )}
                            <td className="px-3 py-2 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-green-500 rounded-full"
                                    style={{ width: `${item.percent_complete}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600">{item.percent_complete}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Educational Footer */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 mb-20 md:mb-0">
        <h3 className="font-semibold text-gray-900 mb-3">💡 Understanding the Methods</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-900">🏔️ Avalanche Method</p>
            <p className="text-gray-600">Pay highest interest rate first. Mathematically optimal—saves the most money in interest over time.</p>
          </div>
          <div>
            <p className="font-medium text-gray-900">⛄ Snowball Method</p>
            <p className="text-gray-600">Pay smallest balance first. Builds momentum through quick wins—great for motivation and behavior change.</p>
          </div>
        </div>
      </div>
      
      {/* Task 4: Sticky Calculate Button for Mobile */}
      {!isStrategySectionVisible && debts.length > 0 && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-lg z-50" data-testid="sticky-calculate-btn">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm">
              <p className="font-semibold text-gray-900">{formatCurrency(totalMonthlyCommitment)}/mo</p>
              <p className="text-xs text-gray-500">{sortedDebts.length} debts • {strategy === 'avalanche' ? '🏔️' : '⛄'}</p>
            </div>
            <button
              onClick={calculatePayoff}
              disabled={calculating}
              className="flex-1 max-w-[200px] py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50"
            >
              {calculating ? '⏳ Calculating...' : '🚀 Calculate'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DebtSnowball;
